import { useState, useEffect, useMemo } from "react";
import api, { apiError } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Loading, Empty } from "@/components/DataState";
import { useAuth } from "@/context/AuthContext";
import { isGestor } from "@/lib/permissions";
import { hms, fmtDate } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Clock, Pencil, Check, X, Utensils } from "lucide-react";

const MEAL_THRESHOLD = 6 * 3600;
const pad = (n) => String(n).padStart(2, "0");
const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStr = () => new Date().toISOString().slice(0, 7);
const lastDay = (y, m) => new Date(Number(y), Number(m), 0).getDate();
const secToHours = (s) => (Math.round(((s || 0) / 3600) * 100) / 100).toString();
const hoursToSec = (h) => Math.max(0, Math.round((parseFloat(h) || 0) * 3600));

const ovKey = (id) => `wh_${id}`;
const loadOv = (id) => { try { return JSON.parse(localStorage.getItem(ovKey(id)) || "{}"); } catch { return {}; } };

export function HoursCounter() {
  const { user } = useAuth();
  const canEdit = isGestor(user);
  const driversQ = useFetch("/admin/drivers");
  const drivers = useMemo(() => driversQ.data?.drivers || [], [driversQ.data]);

  const [tab, setTab] = useState("dia");
  const [day, setDay] = useState(todayStr());
  const [month, setMonth] = useState(monthStr());
  const [half, setHalf] = useState(1);

  const [histories, setHistories] = useState({});
  const [loadingH, setLoadingH] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [modalDriver, setModalDriver] = useState(null);

  // Load all driver histories once (period filtering is client-side)
  useEffect(() => {
    const ids = drivers.map((d) => d.id);
    if (!ids.length) return;
    let active = true;
    setLoadingH(true);
    Promise.all(ids.map((id) => api.get(`/admin/drivers/${id}/history`).then((r) => [id, r.data?.history || []]).catch(() => [id, []])))
      .then((entries) => {
        if (!active) return;
        const map = {}; entries.forEach(([id, h]) => { map[id] = h; });
        setHistories(map);
        const ov = {}; ids.forEach((id) => { ov[id] = loadOv(id); });
        setOverrides(ov);
      })
      .finally(() => { if (active) setLoadingH(false); });
    return () => { active = false; };
  }, [drivers]);

  const [start, end] = useMemo(() => {
    if (tab === "dia") return [day, day];
    const [y, m] = month.split("-");
    if (tab === "quinzena") return half === 1 ? [`${y}-${m}-01`, `${y}-${m}-15`] : [`${y}-${m}-16`, `${y}-${m}-${pad(lastDay(y, m))}`];
    return [`${y}-${m}-01`, `${y}-${m}-${pad(lastDay(y, m))}`];
  }, [tab, day, month, half]);

  const dayVals = (id, h) => {
    const ov = overrides[id]?.[h.date];
    return ov ? { route: ov.route, base: ov.base } : { route: h.route_seconds || 0, base: h.base_seconds || 0 };
  };

  const rows = useMemo(() => {
    const valOf = (id, h) => {
      const ov = overrides[id]?.[h.date];
      return ov ? { route: ov.route, base: ov.base } : { route: h.route_seconds || 0, base: h.base_seconds || 0 };
    };
    return drivers.map((d) => {
      const hs = (histories[d.id] || []).filter((h) => h.date >= start && h.date <= end);
      let route = 0, base = 0;
      hs.forEach((h) => { const v = valOf(d.id, h); route += v.route; base += v.base; });
      return { id: d.id, name: d.name, route, base, total: route + base };
    }).sort((a, b) => b.total - a.total);
  }, [drivers, histories, overrides, start, end]);

  const saveDay = async (driverId, date, routeH, baseH) => {
    const route = hoursToSec(routeH), base = hoursToSec(baseH);
    const total = route + base;
    await api.post("/admin/worked-hours/history", { driver_id: driverId, date, seconds: total });
    // persist route/base split client-side (backend stores only total)
    setOverrides((prev) => {
      const next = { ...prev, [driverId]: { ...(prev[driverId] || {}), [date]: { route, base } } };
      try { localStorage.setItem(ovKey(driverId), JSON.stringify(next[driverId])); } catch (_) {}
      return next;
    });
    // reflect in histories so aggregation updates immediately
    setHistories((prev) => {
      const list = [...(prev[driverId] || [])];
      const idx = list.findIndex((x) => x.date === date);
      if (idx >= 0) list[idx] = { ...list[idx], route_seconds: route, base_seconds: base, worked_seconds: total };
      else list.push({ date, route_seconds: route, base_seconds: base, worked_seconds: total });
      return { ...prev, [driverId]: list };
    });
    toast.success("Horas atualizadas");
  };

  const periodSelector = (
    <div className="flex flex-wrap items-end gap-3">
      {tab === "dia" && (
        <div className="space-y-1">
          <Label className="text-xs text-slate-400">Data</Label>
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="hours-day" className="h-9 border-slate-700 bg-slate-950 text-slate-100" />
        </div>
      )}
      {(tab === "quinzena" || tab === "mes") && (
        <div className="space-y-1">
          <Label className="text-xs text-slate-400">Mês</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="hours-month" className="h-9 border-slate-700 bg-slate-950 text-slate-100" />
        </div>
      )}
      {tab === "quinzena" && (
        <div className="flex overflow-hidden rounded-lg border border-slate-700">
          <button onClick={() => setHalf(1)} data-testid="hours-half-1" className={`px-3 py-1.5 text-sm ${half === 1 ? "bg-orange-500 text-white" : "bg-slate-900 text-slate-300"}`}>1ª quinzena</button>
          <button onClick={() => setHalf(2)} data-testid="hours-half-2" className={`px-3 py-1.5 text-sm ${half === 2 ? "bg-orange-500 text-white" : "bg-slate-900 text-slate-300"}`}>2ª quinzena</button>
        </div>
      )}
      <span className="pb-2 text-xs text-slate-500">Período: {fmtDate(start)} a {fmtDate(end)}</span>
    </div>
  );

  const table = (
    <div className="mt-4 overflow-x-auto rounded-lg border border-slate-800">
      <table className="w-full text-sm" data-testid="hours-table">
        <thead>
          <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="px-4 py-2 font-semibold">Colaborador</th>
            <th className="px-4 py-2 font-semibold">Horas em rota</th>
            <th className="px-4 py-2 font-semibold">Horas em base</th>
            <th className="px-4 py-2 font-semibold">Total</th>
            <th className="px-4 py-2 font-semibold">Vale alimentação</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {rows.length === 0 && <tr><td colSpan={5}><Empty label="Nenhum colaborador" /></td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-slate-800/40" data-testid={`hours-row-${r.id}`}>
              <td className="px-4 py-2">
                <button onClick={() => setModalDriver({ id: r.id, name: r.name })} data-testid={`hours-name-${r.id}`} className="font-medium text-slate-100 underline-offset-2 hover:text-orange-300 hover:underline">{r.name}</button>
              </td>
              <td className="px-4 py-2 font-mono text-slate-300">{hms(r.route)}</td>
              <td className="px-4 py-2 font-mono text-slate-300">{hms(r.base)}</td>
              <td className="px-4 py-2 font-mono font-bold text-white">{hms(r.total)}</td>
              <td className="px-4 py-2">
                {r.total > MEAL_THRESHOLD ? (
                  <span className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-300" data-testid={`hours-meal-${r.id}`}><Utensils className="h-3 w-3" /> Sim</span>
                ) : (
                  <span className="text-xs text-slate-500">Não</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="hours-counter">
      <div className="mb-4 flex items-center gap-2">
        <Clock className="h-4 w-4 text-orange-400" />
        <h3 className="text-base font-medium text-slate-200">Contador de Horas</h3>
      </div>
      {driversQ.loading || loadingH ? <Loading /> : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-950">
            <TabsTrigger value="dia" data-testid="hours-tab-dia">Dia</TabsTrigger>
            <TabsTrigger value="quinzena" data-testid="hours-tab-quinzena">Quinzena</TabsTrigger>
            <TabsTrigger value="mes" data-testid="hours-tab-mes">Mês</TabsTrigger>
          </TabsList>
          <TabsContent value="dia" className="mt-4">{periodSelector}{table}</TabsContent>
          <TabsContent value="quinzena" className="mt-4">{periodSelector}{table}</TabsContent>
          <TabsContent value="mes" className="mt-4">{periodSelector}{table}</TabsContent>
        </Tabs>
      )}

      {modalDriver && (
        <HoursDetailDialog
          driver={modalDriver}
          days={(histories[modalDriver.id] || []).filter((h) => h.date >= start && h.date <= end)}
          dayVals={(h) => dayVals(modalDriver.id, h)}
          canEdit={canEdit}
          onSave={(date, r, b) => saveDay(modalDriver.id, date, r, b)}
          onClose={() => setModalDriver(null)}
        />
      )}
    </div>
  );
}

function HoursDetailDialog({ driver, days, dayVals, canEdit, onSave, onClose }) {
  const [editDate, setEditDate] = useState(null);
  const [routeH, setRouteH] = useState("");
  const [baseH, setBaseH] = useState("");
  const [busy, setBusy] = useState(false);

  const sorted = [...days].sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const totals = sorted.reduce((acc, h) => { const v = dayVals(h); acc.r += v.route; acc.b += v.base; return acc; }, { r: 0, b: 0 });

  const startEdit = (h) => { const v = dayVals(h); setEditDate(h.date); setRouteH(secToHours(v.route)); setBaseH(secToHours(v.base)); };
  const save = async (h) => {
    setBusy(true);
    try { await onSave(h.date, routeH, baseH); setEditDate(null); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl border-slate-800 bg-slate-900" data-testid="hours-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100"><Clock className="h-5 w-5 text-orange-400" /> Horas — {driver.name}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Total no período: rota {hms(totals.r)} · base {hms(totals.b)} · <span className="font-semibold text-white">{hms(totals.r + totals.b)}</span>
            {!canEdit && " · (somente o Gestor pode editar)"}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto rounded-lg border border-slate-800">
          <table className="w-full text-sm" data-testid="hours-detail-table">
            <thead className="sticky top-0 bg-slate-900">
              <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 font-semibold">Data</th>
                <th className="px-3 py-2 font-semibold">Rota</th>
                <th className="px-3 py-2 font-semibold">Base</th>
                <th className="px-3 py-2 font-semibold">Total</th>
                {canEdit && <th className="px-3 py-2 text-right font-semibold">Ações</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {sorted.length === 0 && <tr><td colSpan={canEdit ? 5 : 4}><Empty label="Sem registros no período" /></td></tr>}
              {sorted.map((h) => {
                const v = dayVals(h);
                const editing = editDate === h.date;
                return (
                  <tr key={h.date} className="hover:bg-slate-800/40" data-testid={`hours-detail-row-${h.date}`}>
                    <td className="px-3 py-2 text-slate-300">{fmtDate(h.date)}</td>
                    <td className="px-3 py-2">
                      {editing ? <Input type="number" step="0.25" min="0" value={routeH} onChange={(e) => setRouteH(e.target.value)} data-testid={`hours-route-input-${h.date}`} className="h-8 w-24 border-slate-700 bg-slate-950 text-slate-100" /> : <span className="font-mono text-slate-300">{hms(v.route)}</span>}
                    </td>
                    <td className="px-3 py-2">
                      {editing ? <Input type="number" step="0.25" min="0" value={baseH} onChange={(e) => setBaseH(e.target.value)} data-testid={`hours-base-input-${h.date}`} className="h-8 w-24 border-slate-700 bg-slate-950 text-slate-100" /> : <span className="font-mono text-slate-300">{hms(v.base)}</span>}
                    </td>
                    <td className="px-3 py-2 font-mono font-bold text-white">{editing ? hms(hoursToSec(routeH) + hoursToSec(baseH)) : hms(v.route + v.base)}</td>
                    {canEdit && (
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1.5">
                          {editing ? (
                            <>
                              <Button size="sm" className="h-8 bg-emerald-600 px-2 text-white hover:bg-emerald-700" disabled={busy} onClick={() => save(h)} data-testid={`hours-save-${h.date}`}><Check className="h-4 w-4" /></Button>
                              <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300" disabled={busy} onClick={() => setEditDate(null)} data-testid={`hours-cancel-${h.date}`}><X className="h-4 w-4" /></Button>
                            </>
                          ) : (
                            <Button size="sm" variant="ghost" className="h-8 px-2 text-orange-300 hover:text-orange-200" onClick={() => startEdit(h)} data-testid={`hours-edit-${h.date}`}><Pencil className="h-4 w-4" /> Editar</Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
